/**
 * RepairsPage — Landlord Portal
 *
 * Route: /repairs
 *
 * List all repair tickets with property and status filters.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listRepairs, createRepair, type RepairListRow, type CreateRepairBody } from '../api/repairs.api';
import { listProperties, listUnits, type UnitWithRelations } from '../lib/api';
import type { PropertyWithStats } from '../lib/api';
import { PageHeader } from '../components/layout/index';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { AgentSettingsLink } from '../components/agent/AgentSettingsLink';
import { ThreadUnreadBadge } from '../components/repair/ThreadUnreadBadge';
import { useCaseActivityRefresh } from '../hooks/useCaseActivityRefresh';
import { RepairAgentStateBadge } from '../components/repair/RepairAgentStateBadge';
import { AgentUpgradePrompt } from '../components/billing/AgentUpgradePrompt';
import { useBillingFeatures } from '../hooks/useBilling';
import { formatUnitLabel } from '@resida/shared';

const STATUS_CONFIG = {
  open: { label: 'Open', className: 'bg-info/15 text-info' },
  in_progress: { label: 'In Progress', className: 'bg-warning/15 text-warning' },
  closed: { label: 'Closed', className: 'bg-success/15 text-success' },
} as const;

const CATEGORY_CLASS: Record<string, string> = {
  plumbing: 'bg-info/15 text-info',
  electrical: 'bg-warning/15 text-warning',
  hvac: 'bg-primary/15 text-primary',
  appliance: 'bg-secondary text-secondary-foreground',
  structural: 'bg-destructive/15 text-destructive',
  general: 'bg-muted text-foreground',
};

function RepairStatusBadge({
  status,
  vendorCompletedAt,
}: {
  status: string;
  vendorCompletedAt: string | null;
}) {
  const cfg = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] ?? {
    label: status,
    className: 'bg-muted text-foreground',
  };
  return (
    <div>
      <Badge className={cfg.className} variant="secondary">
        {cfg.label}
      </Badge>
      {status === 'in_progress' && vendorCompletedAt && (
        <div className="text-[11px] text-warning mt-0.5">
          Awaiting Confirmation
        </div>
      )}
    </div>
  );
}

function RepairCategoryBadge({ category }: { category: string }) {
  const cls = CATEGORY_CLASS[category] ?? 'bg-muted text-foreground';
  return (
    <Badge className={cls} variant="secondary">
      {category}
    </Badge>
  );
}

const EMPTY_FORM: CreateRepairBody = {
  title: '',
  description: '',
  category: 'general',
  priority: 'routine',
  unitId: '',
};

interface CreateRepairModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (repairId: string) => void;
}

function CreateRepairModal({ open, onClose, onCreated }: CreateRepairModalProps) {
  const [form, setForm] = useState<CreateRepairBody & { tenantId: string }>(
    { ...EMPTY_FORM, tenantId: '' },
  );
  const [units, setUnits] = useState<UnitWithRelations[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedRef = useRef(false);

  // Fetch units lazily when modal opens
  useEffect(() => {
    if (!open) return;
    if (loadedRef.current) return;
    loadedRef.current = true;
    setUnitsLoading(true);
    listUnits()
      .then((res) => setUnits(res.units))
      .catch(() => setUnits([]))
      .finally(() => setUnitsLoading(false));
  }, [open]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setForm({ ...EMPTY_FORM, tenantId: '' });
      setError(null);
      loadedRef.current = false;
    }
  }, [open]);

  const isValid = form.title.trim() !== '' && form.description.trim() !== '' && form.unitId !== '';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: CreateRepairBody = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        priority: form.priority,
        unitId: form.unitId,
        ...(form.tenantId.trim() ? { tenantId: form.tenantId.trim() } : {}),
      };
      const { repair } = await createRepair(body);
      onCreated(repair.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create repair request');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent
        data-testid="create-repair-modal"
        aria-describedby={undefined}
        className="max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>Create Repair Request</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Title */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="cr-title" className="text-sm font-medium">
              Title <span className="text-destructive">*</span>
            </label>
            <input
              id="cr-title"
              data-testid="title-input"
              type="text"
              required
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Brief description of the issue"
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
            />
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="cr-description" className="text-sm font-medium">
              Description <span className="text-destructive">*</span>
            </label>
            <textarea
              id="cr-description"
              data-testid="description-input"
              required
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Describe the issue in detail"
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          {/* Category */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="cr-category" className="text-sm font-medium">
              Category
            </label>
            <select
              id="cr-category"
              data-testid="category-select"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as CreateRepairBody['category'] }))}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
            >
              <option value="plumbing">Plumbing</option>
              <option value="electrical">Electrical</option>
              <option value="hvac">HVAC</option>
              <option value="appliance">Appliance</option>
              <option value="structural">Structural</option>
              <option value="general">General</option>
            </select>
          </div>

          {/* Priority */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="cr-priority" className="text-sm font-medium">
              Priority
            </label>
            <select
              id="cr-priority"
              data-testid="priority-select"
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value as CreateRepairBody['priority'] }))}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
            >
              <option value="emergency">Emergency</option>
              <option value="routine">Routine</option>
              <option value="minor">Minor</option>
            </select>
          </div>

          {/* Unit */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="cr-unit" className="text-sm font-medium">
              Unit <span className="text-destructive">*</span>
            </label>
            <select
              id="cr-unit"
              data-testid="unit-select"
              required
              value={form.unitId}
              onChange={(e) => setForm((f) => ({ ...f, unitId: e.target.value }))}
              disabled={unitsLoading}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring min-h-[44px] disabled:opacity-50"
              aria-busy={unitsLoading}
            >
              <option value="">
                {unitsLoading ? 'Loading units…' : 'Select a unit'}
              </option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {`${unit.property?.name ?? 'Unknown'}${formatUnitLabel(unit.unitNumber) ? ` — ${formatUnitLabel(unit.unitNumber)}` : ''}`}
                </option>
              ))}
            </select>
          </div>

          {/* Tenant ID (optional) */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="cr-tenant-id" className="text-sm font-medium">
              Tenant ID
            </label>
            <input
              id="cr-tenant-id"
              data-testid="tenant-id-input"
              type="text"
              value={form.tenantId}
              onChange={(e) => setForm((f) => ({ ...f, tenantId: e.target.value }))}
              placeholder="Tenant ID (optional)"
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
            />
          </div>

          {/* Inline error */}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isValid || submitting}
              data-testid="submit-confirm-button"
            >
              {submitting ? 'Creating…' : 'Create Request'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RepairsPage() {
  const navigate = useNavigate();
  const { data: featuresData } = useBillingFeatures();
  const hasAgenticSupport = featuresData?.features.includes('agentic_support') ?? false;
  const [repairs, setRepairs] = useState<RepairListRow[]>([]);
  const [properties, setProperties] = useState<PropertyWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // `silent` (live refresh) keeps the list on screen — no skeleton swap.
  const loadRepairs = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    if (!opts?.silent) setError(null);
    try {
      const result = await listRepairs({
        propertyId: selectedPropertyId || undefined,
        status: selectedStatus || undefined,
      });
      setRepairs(result.repairs);
    } catch (err) {
      // A silent background refresh keeps the still-valid list and raises no alert.
      if (!opts?.silent) setError(err instanceof Error ? err.message : 'Failed to load repairs');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [selectedPropertyId, selectedStatus]);

  // RA-LIVE-REFRESH — new messages / reads elsewhere update the unread badges in place.
  useCaseActivityRefresh({ onChange: () => void loadRepairs({ silent: true }) });

  useEffect(() => {
    async function init() {
      setPropertiesLoading(true);
      try {
        const res = await listProperties();
        setProperties(res.properties);
      } catch {
        // non-fatal
      } finally {
        setPropertiesLoading(false);
      }
    }
    init();
  }, []);

  useEffect(() => {
    loadRepairs();
  }, [loadRepairs]);

  return (
    <div className="max-w-4xl mx-auto pb-6" data-testid="repairs-page">
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Repairs"
          subtitle="Repairs across your portfolio."
          action={
            <Button
              onClick={() => setCreateModalOpen(true)}
              data-testid="create-repair-button"
            >
              Create Request
            </Button>
          }
        />
      </div>

      <CreateRepairModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={(repairId) => {
          setCreateModalOpen(false);
          navigate(`/repairs/${repairId}`);
        }}
      />

      {/* Agent settings — defaults and playbook live under Settings → AI agents. Without agent
          support the landlord sees the upgrade prompt instead; their existing cases stay listed
          (read-only) below. */}
      {featuresData !== undefined && (
        <div className="px-[var(--page-padding-x)] mb-4">
          {hasAgenticSupport ? <AgentSettingsLink agentKind="repair" /> : <AgentUpgradePrompt showPastCasesNote />}
        </div>
      )}

      {/* Filters */}
      <div className="px-[var(--page-padding-x)] mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[160px]">
            <label
              htmlFor="repairs-property-filter"
              className="block text-xs font-medium text-muted-foreground mb-1"
            >
              Property
            </label>
            <select
              id="repairs-property-filter"
              data-testid="repairs-property-filter"
              value={selectedPropertyId}
              onChange={(e) => setSelectedPropertyId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[44px]"
              aria-label="Filter by property"
              disabled={propertiesLoading}
            >
              <option value="">All Properties</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[140px]">
            <label
              htmlFor="repairs-status-filter"
              className="block text-xs font-medium text-muted-foreground mb-1"
            >
              Status
            </label>
            <select
              id="repairs-status-filter"
              data-testid="repairs-status-filter"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[44px]"
              aria-label="Filter by status"
            >
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <Button
            onClick={() => void loadRepairs()}
            disabled={loading}
            data-testid="repairs-apply-filter"
            aria-label="Apply filters"
            className="min-h-[44px]"
          >
            {loading ? 'Loading...' : 'Apply'}
          </Button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div
          role="alert"
          className="mx-[var(--page-padding-x)] mb-4 p-3 bg-destructive/5 border border-destructive/30 rounded-lg text-sm text-destructive flex items-center gap-3"
        >
          <span className="flex-1">{error}</span>
          <button
            type="button"
            className="text-sm text-destructive hover:underline"
            onClick={() => void loadRepairs()}
          >
            Retry
          </button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="px-[var(--page-padding-x)] space-y-2" aria-label="Loading repairs" aria-busy="true">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      ) : repairs.length === 0 ? (
        <div className="px-[var(--page-padding-x)] py-10 text-center text-muted-foreground">
          <p>No repair tickets found for the selected filters.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSelectedPropertyId('');
              setSelectedStatus('');
            }}
            className="mt-3"
            data-testid="repairs-clear-filters"
          >
            Clear Filters
          </Button>
        </div>
      ) : (
        <div
          data-testid="repairs-list"
          className="flex flex-col"
          aria-label="Repair tickets"
        >
          {repairs.map((repair) => (
            <div
              key={repair.id}
              data-testid={`repair-list-item-${repair.id}`}
              className="flex flex-col gap-1.5 px-[var(--page-padding-x)] py-3 border-b border-border last:border-b-0"
            >
              <div className="flex items-start justify-between gap-2">
                <Link
                  to={`/repairs/${repair.id}`}
                  className="text-sm font-medium text-foreground hover:text-primary"
                  data-testid={`repair-list-item-link-${repair.id}`}
                >
                  {repair.title}
                </Link>
                <div className="flex items-center gap-2">
                  <ThreadUnreadBadge
                    count={repair.unreadCount}
                    testId={`repair-list-unread-${repair.id}`}
                  />
                  <RepairStatusBadge
                    status={repair.status}
                    vendorCompletedAt={repair.vendorCompletedAt}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* RA-AGENT-STATE-LANDLORD-ONLY — populated by list()'s landlord-scoped
                    bulk resolution; distinct from the status badge above, never replacing it. */}
                <RepairAgentStateBadge agentState={repair.agentState} />
                {/* RA-CATEGORIZE-GATE: category/priority are nullable — a tenant-submitted
                    ticket may still be pending_triage with neither set. */}
                {repair.category && <RepairCategoryBadge category={repair.category} />}
                {repair.priority && (
                  <span className="text-xs text-muted-foreground">{repair.priority}</span>
                )}
                {repair.scheduledAt && (
                  <span className="text-xs text-muted-foreground">
                    Scheduled: {new Date(repair.scheduledAt).toLocaleDateString()}
                  </span>
                )}
                {repair.assignedVendorId && (
                  <span className="text-xs text-muted-foreground">Vendor assigned</span>
                )}
                <span className="text-xs text-muted-foreground ml-auto">
                  {new Date(repair.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RepairsPage;
